/**
 * Cart Delivery Date Picker with Calendar UI
 *
 * 既存のShippingCalendarクラスを活用した配達希望日選択カレンダー
 * テーマ設定から営業日ルールを取得し、配達可能日のみ選択可能にする
 */

class CartDeliveryDatePicker {
  constructor(inputId, options = {}) {
    this.input = document.getElementById(inputId);
    if (!this.input) {
      console.warn('[CartDeliveryDatePicker] Input element not found:', inputId);
      return;
    }

    // ShippingCalendarインスタンスを作成
    this.calendar = new window.ShippingCalendar({
      leadDays: options.leadDays || 2,
      cutoffTime: options.cutoffTime || '12:00',
      closedWeekdays: options.closedWeekdays || '0,6',
      holidays: options.holidays || ''
    });

    // 配送日数を保存
    this.deliveryLeadDays = options.deliveryLeadDays || 1;

    // 現在の年月
    const today = new Date();
    this.currentYear = today.getFullYear();
    this.currentMonth = today.getMonth() + 1;

    // 最短配達日を計算（発送日 + 配送日数）
    this.minDeliveryDate = this.calendar.calculateShippingDate(today);

    if (!this.minDeliveryDate) {
      console.error('[CartDeliveryDatePicker] 最短配達日の計算に失敗しました');
      return;
    }

    // 配送日数を加算（発送日から配達日までの日数）
    this.minDeliveryDate.setDate(this.minDeliveryDate.getDate() + this.deliveryLeadDays);

    this.selectedDate = this.input.value || null;

    this.init();
  }

  init() {
    // カレンダーポップアップを作成
    this.createCalendarPopup();

    // 入力欄のイベント
    this.input.addEventListener('click', () => this.showCalendar());
    this.input.readOnly = true; // 直接入力を防止

    // 外部クリックで閉じる
    this.closeHandler = (e) => {
      if (!this.popup.contains(e.target) && e.target !== this.input) {
        this.hideCalendar();
      }
    };
  }

  createCalendarPopup() {
    this.popup = document.createElement('div');
    this.popup.className = 'delivery-calendar-popup';
    this.popup.style.display = 'none';

    // ナビゲーションボタン
    const nav = document.createElement('div');
    nav.className = 'delivery-calendar-nav';
    nav.innerHTML = `
      <button type="button" class="delivery-calendar-nav__prev">← 前月</button>
      <span class="delivery-calendar-nav__title"></span>
      <button type="button" class="delivery-calendar-nav__next">次月 →</button>
    `;

    // カレンダーコンテンツ
    const content = document.createElement('div');
    content.className = 'delivery-calendar-content';

    // 凡例
    const legend = document.createElement('div');
    legend.className = 'delivery-calendar-legend';
    legend.innerHTML = `
      <div class="legend-item">
        <span class="legend-box legend-box--deliverable"></span>
        <span class="legend-text">配達可能日</span>
      </div>
      <div class="legend-item">
        <span class="legend-box legend-box--not-deliverable"></span>
        <span class="legend-text">配達不可日</span>
      </div>
    `;

    this.popup.appendChild(nav);
    this.popup.appendChild(content);
    this.popup.appendChild(legend);
    this.input.parentNode.style.position = 'relative';
    this.input.parentNode.appendChild(this.popup);

    // ナビゲーションイベント
    this.prevBtn = nav.querySelector('.delivery-calendar-nav__prev');
    this.nextBtn = nav.querySelector('.delivery-calendar-nav__next');
    this.titleSpan = nav.querySelector('.delivery-calendar-nav__title');

    this.prevBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.currentMonth--;
      if (this.currentMonth < 1) {
        this.currentMonth = 12;
        this.currentYear--;
      }
      this.renderCalendar();
    });

    this.nextBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.currentMonth++;
      if (this.currentMonth > 12) {
        this.currentMonth = 1;
        this.currentYear++;
      }
      this.renderCalendar();
    });
  }

  showCalendar() {
    this.popup.style.display = 'block';

    // 選択済みの日付がある場合、その月を表示
    if (this.selectedDate) {
      const selected = new Date(this.selectedDate);
      this.currentYear = selected.getFullYear();
      this.currentMonth = selected.getMonth() + 1;
    } else {
      // 最短配達日の月を表示
      this.currentYear = this.minDeliveryDate.getFullYear();
      this.currentMonth = this.minDeliveryDate.getMonth() + 1;
    }

    this.renderCalendar();

    // 外部クリックハンドラを登録
    setTimeout(() => {
      document.addEventListener('click', this.closeHandler);
    }, 0);
  }

  hideCalendar() {
    this.popup.style.display = 'none';
    document.removeEventListener('click', this.closeHandler);
  }

  renderCalendar() {
    // 既存のShippingCalendarクラスでHTML生成
    const calendarHTML = this.calendar.generateCalendarHTML(
      this.currentYear,
      this.currentMonth
    );

    const content = this.popup.querySelector('.delivery-calendar-content');
    content.innerHTML = calendarHTML;

    // タイトル更新
    this.titleSpan.textContent = `${this.currentYear}年${this.currentMonth}月`;

    // 日付セルを処理
    this.processCells();
  }

  processCells() {
    const cells = this.popup.querySelectorAll('.shipping-calendar__table td:not(.is-empty)');

    cells.forEach(cell => {
      const dayText = cell.querySelector('.shipping-calendar__day');
      if (!dayText) return;

      const day = parseInt(dayText.textContent);
      if (isNaN(day)) return;

      const date = new Date(this.currentYear, this.currentMonth - 1, day);
      const dateStr = this.calendar.formatDate(date, 'YYYY-MM-DD');

      // 配達可能日判定
      const isDeliverable = this.isDeliverableDate(date);

      // 既存のis-open/is-closedクラスを削除
      cell.classList.remove('is-open', 'is-closed');

      if (isDeliverable) {
        cell.classList.add('is-deliverable');
        cell.dataset.date = dateStr;
        cell.style.cursor = 'pointer';

        // 選択済みの日付をハイライト
        if (this.selectedDate === dateStr) {
          cell.classList.add('is-selected');
        }

        // クリックイベント
        cell.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.selectDate(dateStr);
        });
      } else {
        cell.classList.add('is-not-deliverable');
        cell.style.cursor = 'not-allowed';
      }
    });
  }

  isDeliverableDate(date) {
    // 1. 最短配達日より前 → NG
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const minDateOnly = new Date(this.minDeliveryDate.getFullYear(), this.minDeliveryDate.getMonth(), this.minDeliveryDate.getDate());

    if (dateOnly < minDateOnly) {
      return false;
    }

    // 2. 営業日チェック（定休日・臨時休業日を除外）
    if (!this.calendar.isBusinessDay(date)) {
      return false;
    }

    return true;
  }

  selectDate(dateStr) {
    // 入力欄に値を設定
    this.input.value = dateStr;
    this.selectedDate = dateStr;

    // カレンダーを閉じる
    this.hideCalendar();

    // changeイベントを発火（他の処理との連携用）
    const event = new Event('change', { bubbles: true });
    this.input.dispatchEvent(event);
  }
}

// グローバルスコープに公開（Liquidから使用可能にする）
if (typeof window.CartDeliveryDatePicker === 'undefined') {
  window.CartDeliveryDatePicker = CartDeliveryDatePicker;
}
